import { memo, ReactElement, useState, useEffect } from 'react';

import { Timestamp } from 'firebase/firestore';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';

import dayjs, { Dayjs } from 'dayjs';

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import { MIME_TYPES } from '../../enums/MimeEnum';
import Event, { EVENT_COST_PAID_BY, CostPaidBy, EVENT_REG_TYPES, EventRegType, PARTICIPANT_ORDERS } from '../../types/Event';
import { HANDICAP_LABELS } from '../../utils/handicapUtils';
import { ToDayJS } from '../../utils/utils';
import GratiDateTimeField from '../common/GratiDateTimeField';
import GratiFileUpload from '../common/GratiFileUpload';

import EventPlayerChoiceLockField from './EventPlayerChoiceLockField';
import { FieldTarget } from './eventPreviewTypes';
import EventPriorityEditor from './EventPriorityEditor';
import EventRosterDeadlineEditor from './EventRosterDeadlineEditor';

// Helper to normalize deprecated registration type values
const normalizeRegType = (regType: EventRegType): EventRegType => {
  if (regType === EVENT_REG_TYPES.DEPRECATED_PLAYER_NO_TEE_TIME) {
    return EVENT_REG_TYPES.PLAYER_NO_TEE_TIME;
  }
  return regType;
};


type EventSignupTimeEditorProps = {
  event: Event;
  isGolf: boolean;
  isTeamLeague?: boolean;
  onUpdate: (updates: Partial<Event>) => void;
  /** Called for explicit user-driven ghinIndexDate changes (DatePicker + clear). Does NOT fire for auto-adjustments. */
  onGhinIndexDateChange?: (date: Timestamp | undefined) => void;
  focusTarget?: FieldTarget | null;
  onFocusComplete?: () => void;
};

const EventSignupTimeEditor = memo(function EventSignupTimeEditor({
  event,
  isGolf,
  isTeamLeague = false,
  onUpdate,
  onGhinIndexDateChange,
  focusTarget,
  onFocusComplete,
}: EventSignupTimeEditorProps): ReactElement {
  useEffect(() => {
    if (focusTarget === 'cost') {
      onFocusComplete?.();
    }
  }, [focusTarget, onFocusComplete]);
  const signupOpensAt = event.signupOpensAt instanceof Timestamp ? event.signupOpensAt : null;
  const signupClosesAt = event.signupClosesAt instanceof Timestamp ? event.signupClosesAt : null;

  // GHIN Index Date state - start in custom mode if ghinIndexDate is present
  const [isCustomGhinDate, setIsCustomGhinDate] = useState(!!event.ghinIndexDate);
  const ghinIndexDate = event.ghinIndexDate instanceof Timestamp ? event.ghinIndexDate : null;
  const defaultDate = event.days[0]?.datetime instanceof Timestamp ? event.days[0].datetime : null;

  // Watch for changes to days[0].date and adjust ghinIndexDate if needed
  useEffect(() => {
    if (ghinIndexDate && defaultDate && ghinIndexDate.toDate() > defaultDate.toDate()) {
      onUpdate({
        ghinIndexDate: defaultDate,
      });
    }
  }, [defaultDate, ghinIndexDate, onUpdate]);

  const handleClearGhinDate = () => {
    if (onGhinIndexDateChange) {
      onGhinIndexDateChange(undefined);
    } else {
      onUpdate({ ghinIndexDate: undefined });
    }
    setIsCustomGhinDate(false);
  };

  const handleChangeGhinDate = () => {
    // When switching to custom date, initialize with the default date if no custom date is set
    if (!ghinIndexDate && defaultDate) {
      onUpdate({
        ghinIndexDate: defaultDate,
      });
    }
    setIsCustomGhinDate(true);
  };

  const handleRegFormAdded = (fileName: string) => {
    onUpdate({ registrationExternalForm: fileName });
  };

  const handleRegFormDeleted = () => {
    onUpdate({ registrationExternalForm: undefined });
  };

  const handleEmailAttachmentAdded = (fileName: string) => {
    onUpdate({ registrationEmailAttachment: fileName });
  };

  const handleEmailAttachmentDeleted = () => {
    onUpdate({ registrationEmailAttachment: undefined });
  };

  return (
    <Stack spacing={2} sx={{ display: 'flex' }}>
      <Stack direction="row" spacing={2} sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography variant="bodyLarge">Signup using:</Typography>
        <ToggleButtonGroup
          aria-label="Signup method"
          color="primary"
          value={normalizeRegType(event.registrationType)}
          exclusive
          onChange={(_, value) => {
            if (value) {
              onUpdate({
                registrationType: value,
              });
            }
          }}
          size="small"
        >
          <ToggleButton value={EVENT_REG_TYPES.PLAYER_NO_TEE_TIME}>App</ToggleButton>
          <ToggleButton value={EVENT_REG_TYPES.EMAIL}>Email</ToggleButton>
          <ToggleButton value={EVENT_REG_TYPES.EXTERNAL}>Mail</ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ alignItems: 'flex-start' }}>
        <GratiDateTimeField
          label="Sign up opens"
          value={signupOpensAt}
          timezone={event.timezone}
          defaultTime="start"
          onChange={(opensAt) => onUpdate({
            signupOpensAt: opensAt,
            signupClosesAt: signupClosesAt && opensAt > signupClosesAt ? opensAt : event.signupClosesAt,
          })}
        />
        <GratiDateTimeField
          label="Sign up closes"
          value={signupClosesAt}
          timezone={event.timezone}
          defaultTime="end"
          onChange={(closesAt) => onUpdate({ signupClosesAt: closesAt })}
        />
      </Stack>
      {event.registrationType === EVENT_REG_TYPES.EXTERNAL && (
        <Box>
          <GratiFileUpload
            onFileAdded={handleRegFormAdded}
            onFileDeleted={handleRegFormDeleted}
            fileNames={event.registrationExternalForm ? [event.registrationExternalForm] : []}
            storageFilePrefix={event.itemId}
            storageFileSuffix={'reg'}
            storageLocation=
              {`/org/${event.orgId}/league/${event.leagueId}/event/${event.itemId}/orgMemberLeagueAdminFile`}
            allowedTypes={[MIME_TYPES.PDF]}
            thumbnailSize="small"
            label={event.registrationExternalForm ? null : 'Registration form'}
            defaultDisplayName="Registration Form"
          />
        </Box>
      )}
      <Stack spacing={1}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' } }}>
          {event.registrationType === EVENT_REG_TYPES.EMAIL && (
            <Typography variant="bodyLarge">Register with</Typography>
          )}
          <TextField
            label={event.registrationType === EVENT_REG_TYPES.EMAIL ? 'Email' : 'Contact email'}
            value={event.registrationEmail || ''}
            onChange={(e) => onUpdate({ registrationEmail: e.target.value || undefined })}
          />
        </Stack>
        {event.registrationType === EVENT_REG_TYPES.EMAIL && (
          <Box>
            <GratiFileUpload
              onFileAdded={handleEmailAttachmentAdded}
              onFileDeleted={handleEmailAttachmentDeleted}
              fileNames={event.registrationEmailAttachment ? [event.registrationEmailAttachment] : []}
              storageFilePrefix={event.itemId}
              storageFileSuffix={'email-attach'}
              storageLocation={`/org/${event.orgId}/league/${event.leagueId}/event/${event.itemId}/orgMemberLeagueAdminFile`}
              allowedTypes={[MIME_TYPES.PDF]}
              thumbnailSize="small"
              label={event.registrationEmailAttachment ? null : 'Attachment'}
              defaultDisplayName="Registration Attachment"
            />
          </Box>
        )}
      </Stack>
      {isGolf && (
        <EventPriorityEditor
          value={event.participantOrder}
          onChange={(value) => onUpdate({ participantOrder: value ?? PARTICIPANT_ORDERS.SIGNUP_TIME })}
          label="Signup priority:"
          fallbackValue={PARTICIPANT_ORDERS.SIGNUP_TIME}
          showMgrChoice={isTeamLeague}
        />
      )}
      {isGolf && event.participantOrder === PARTICIPANT_ORDERS.DRAWING && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{
          alignItems: { xs: 'flex-start', sm: 'center' }
        }}>
          <Typography variant="bodyMedium">Draw date &amp; time:</Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateTimePicker
              value={event.rosterLotteryDate instanceof Timestamp ? dayjs(event.rosterLotteryDate.toDate()) : null}
              onChange={(dt: Dayjs | null) => {
                onUpdate({ rosterLotteryDate: dt ? Timestamp.fromDate(dt.toDate()) : undefined });
              }}
              maxDateTime={defaultDate ? dayjs(defaultDate.toDate()) : undefined}
              slotProps={{ textField: { size: 'small' } }}
            />
          </LocalizationProvider>
        </Stack>
      )}
      {isGolf && event.participantOrder === PARTICIPANT_ORDERS.MGR_CHOICE && (
        <EventRosterDeadlineEditor event={event} onUpdate={onUpdate} defaultDate={defaultDate} />
      )}
      {isGolf && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{
          alignItems: { xs: 'flex-start', sm: 'center' }
        }}>
          <Typography variant="bodyMedium">{HANDICAP_LABELS.INDEX_DATE}:</Typography>
          {!isCustomGhinDate ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                size="small"
                disabled
                value={defaultDate ? dayjs(defaultDate.toDate()).format('MMM D, YYYY') : 'No date set'}
              />
              <Button size="small" onClick={handleChangeGhinDate}>
                Change
              </Button>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={ghinIndexDate ? dayjs(ghinIndexDate.toDate()) : defaultDate ? dayjs(defaultDate.toDate()) : null}
                  onChange={(date: Dayjs | null) => {
                    const resolved = date && defaultDate && date.toDate() > defaultDate.toDate()
                      ? defaultDate
                      : date ? Timestamp.fromDate(date.toDate()) : undefined;
                    if (onGhinIndexDateChange) {
                      onGhinIndexDateChange(resolved);
                    } else {
                      onUpdate({ ghinIndexDate: resolved });
                    }
                  }}
                  maxDate={defaultDate ? dayjs(defaultDate.toDate()) : undefined}
                  slotProps={{
                    textField: {
                      size: 'small',
                    },
                  }}
                />
              </LocalizationProvider>
              <Button size="small" onClick={handleClearGhinDate}>
                Clear
              </Button>
            </Box>
          )}
        </Stack>
      )}
      {isGolf && <EventPlayerChoiceLockField event={event} onUpdate={onUpdate} />}
      <Stack spacing={1}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ alignItems: { xs: 'flex-start', sm: 'center' } }}>
          <TextField
            label="Entry fee"
            value={event.cost || ''}
            onChange={(e) => onUpdate({ cost: e.target.value || undefined })}
            placeholder="e.g. $25"
            sx={{ maxWidth: '12em' }}
          />
          {event.cost && (
            <ToggleButtonGroup
              aria-label="Cost charged by"
              color="primary"
              value={event.costChargedBy ?? EVENT_COST_PAID_BY.OTHER}
              exclusive
              size="small"
              onChange={(_, value: CostPaidBy) => {
                if (!value) return;
                const updates: Partial<Event> = { costChargedBy: value };
                if (value === EVENT_COST_PAID_BY.ACCOUNT && !event.costChargedOn) {
                  updates.costChargedOn = signupClosesAt ?? undefined;
                }
                onUpdate(updates);
              }}
            >
              <ToggleButton value={EVENT_COST_PAID_BY.ACCOUNT}>Account</ToggleButton>
              <ToggleButton value={EVENT_COST_PAID_BY.CHECK}>Check</ToggleButton>
              <ToggleButton value={EVENT_COST_PAID_BY.OTHER}>Other</ToggleButton>
            </ToggleButtonGroup>
          )}
        </Stack>
        {event.cost && event.costChargedBy === EVENT_COST_PAID_BY.ACCOUNT && (
          <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
            <Typography variant="bodyMedium">Charge on:</Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DatePicker
                  value={
                    event.costChargedOn instanceof Timestamp
                      ? ToDayJS(event.costChargedOn, event.timezone)
                      : signupClosesAt
                      ? ToDayJS(signupClosesAt, event.timezone)
                      : null
                  }
                  onChange={(dt: Dayjs | null) => {
                    if (!dt) { onUpdate({ costChargedOn: undefined }); return; }
                    const tz = event.timezone || undefined;
                    const dateInTz = tz ? dayjs.tz(dt.format('YYYY-MM-DD'), tz) : dt.startOf('day');
                    onUpdate({ costChargedOn: Timestamp.fromDate(dateInTz.toDate()) });
                  }}
                  slotProps={{ textField: { size: 'small' } }}
                />
                {event.costChargedOn instanceof Timestamp && (
                  <Button size="small" onClick={() => onUpdate({ costChargedOn: undefined })}>
                    Clear
                  </Button>
                )}
              </Box>
            </LocalizationProvider>
          </Stack>
        )}
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: { xs: 'flex-start', sm: 'center' } }}>
        <Typography variant="bodyMedium">Roster visible to all:</Typography>
        <ToggleButtonGroup
          value={event.showRosterToAllDate ? 'date' : 'always'}
          exclusive
          size="small"
          onChange={(_, val) => {
            if (val === 'always') onUpdate({ showRosterToAllDate: undefined, showRosterToAllDateManuallySet: true });
            else if (val === 'date') onUpdate({ showRosterToAllDate: Timestamp.fromDate(new Date()), showRosterToAllDateManuallySet: true });
          }}
        >
          <ToggleButton value="always">Always</ToggleButton>
          <ToggleButton value="date">On date</ToggleButton>
        </ToggleButtonGroup>
        {event.showRosterToAllDate instanceof Timestamp && (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              value={dayjs(event.showRosterToAllDate.toDate())}
              onChange={(dt: Dayjs | null) => {
                onUpdate({ showRosterToAllDate: dt ? Timestamp.fromDate(dt.startOf('day').toDate()) : undefined, showRosterToAllDateManuallySet: true });
              }}
              slotProps={{ textField: { size: 'small' } }}
            />
          </LocalizationProvider>
        )}
      </Stack>
      {isTeamLeague && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: { xs: 'flex-start', sm: 'center' } }}>
          <Typography variant="bodyMedium">Team visible to members:</Typography>
          <ToggleButtonGroup
            value={event.showRosterToTeamDate ? 'date' : 'always'}
            exclusive
            size="small"
            onChange={(_, val) => {
              if (val === 'always') onUpdate({ showRosterToTeamDate: undefined, showRosterToTeamDateManuallySet: true });
              else if (val === 'date') onUpdate({ showRosterToTeamDate: Timestamp.fromDate(new Date()), showRosterToTeamDateManuallySet: true });
            }}
          >
            <ToggleButton value="always">Always</ToggleButton>
            <ToggleButton value="date">On date</ToggleButton>
          </ToggleButtonGroup>
          {event.showRosterToTeamDate instanceof Timestamp && (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={dayjs(event.showRosterToTeamDate.toDate())}
                onChange={(dt: Dayjs | null) => {
                  onUpdate({ showRosterToTeamDate: dt ? Timestamp.fromDate(dt.startOf('day').toDate()) : undefined, showRosterToTeamDateManuallySet: true });
                }}
                slotProps={{ textField: { size: 'small' } }}
              />
            </LocalizationProvider>
          )}
        </Stack>
      )}
    </Stack>
  );
});

export default EventSignupTimeEditor;
